
import { supabase } from './authService';
import { CommentWithUser } from '../types';
import { fetchTeamInfo, getTeamLogo } from './footballService';

const augmentCommentsWithFavoriteTeam = async (comments: any[]): Promise<CommentWithUser[]> => {
  if (!comments || comments.length === 0) return [];
  
  const userIds = Array.from(new Set(comments.map(c => c.user_id)));
  
  // Get all favorite teams for these users
  const { data: selections } = await supabase
    .from('teams_selections')
    .select('user_id, team_id')
    .in('user_id', userIds)
    .eq('is_favorite', true);

  const teamMap = new Map();
  if (selections) {
    // Collect unique team IDs to fetch info
    const teamIds = Array.from(new Set(selections.map(s => s.team_id)));
    const teamInfos = await Promise.all(teamIds.map(id => fetchTeamInfo(id)));
    
    const teamDataMap = new Map();
    teamInfos.forEach(info => {
      if (info) {
        const teamId = info.team.id.toString();
        teamDataMap.set(teamId, {
          name: info.team.name,
          logo: getTeamLogo(teamId)
        });
      }
    });

    selections.forEach(s => {
      teamMap.set(s.user_id, teamDataMap.get(s.team_id.toString()));
    });
  }

  return comments.map(c => ({
    ...c,
    user: c.user ? {
      ...c.user,
      favorite_team: teamMap.get(c.user_id) || null
    } : undefined
  }));
};

export const createComment = async ({
  postId,
  content,
  imageUrl = null,
  parentId = null,
}: {
  postId: string;
  content: string;
  imageUrl?: string | null;
  parentId?: string | null;
}) => {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) throw new Error('로그인이 필요합니다');

  const { data, error } = await supabase
    .from('comments')
    .insert({
      post_id: postId,
      user_id: user.id,
      content,
      image_url: imageUrl,
      parent_id: parentId,
    })
    .select('*, user:user(name, avatar_url)')
    .single();

  if (error) {
    console.error("Create comment error:", error);
    throw error;
  }
  const augmented = await augmentCommentsWithFavoriteTeam([data]);
  return { ...augmented[0], is_liked: false, likes_count: 0 };
};

export const fetchComments = async (postId: string): Promise<(CommentWithUser & { is_liked?: boolean })[]> => {
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('comments')
    .select('*, user:user(name, avatar_url), comment_likes(user_id)')
    .eq('post_id', postId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error("Fetch comments error details:", JSON.stringify(error, null, 2));
    return [];
  }

  const augmented = await augmentCommentsWithFavoriteTeam(data || []);

  return augmented.map(comment => {
    const rawComment = (data || []).find(d => d.id === comment.id);
    const likes = rawComment?.comment_likes || [];
    return {
      ...comment,
      likes_count: likes.length,
      is_liked: user ? likes.some((l: any) => l.user_id === user.id) : false
    };
  });
};

/**
 * 특정 유저가 작성한 모든 댓글을 가져옵니다.
 */
export const fetchUserComments = async (userId: string): Promise<CommentWithUser[]> => {
  const { data, error } = await supabase
    .from('comments')
    .select('*, user:user(name, avatar_url)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error("Fetch user comments error:", error);
    return [];
  }

  return augmentCommentsWithFavoriteTeam(data || []);
};

export const deleteComment = async (commentId: string) => {
  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId);
  if (error) throw error;
};

export const likeComment = async (commentId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('로그인 필요');

  const { error } = await supabase.from('comment_likes').insert({
    comment_id: commentId,
    user_id: user.id,
  });
  
  if (error) throw error;
};

export const unlikeComment = async (commentId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('로그인 필요');

  const { error } = await supabase.from('comment_likes').delete()
    .eq('comment_id', commentId)
    .eq('user_id', user.id);
    
  if (error) throw error;
};

/**
 * 전역 커뮤니티 피드를 위한 전체 최신 댓글을 가져옵니다.
 */
export const fetchAllRecentComments = async (limit = 50): Promise<(CommentWithUser & { is_liked?: boolean, match_info?: any })[]> => {
  const { data: { user } } = await supabase.auth.getUser();

  // post_id가 숫자로 된 것(경기 fixture ID)만 가져오기 시도
  // 참고: 현재 comments 테이블 구조상 post_id는 string이지만 경기 댓글은 fixture ID를 담고 있음
  const { data, error } = await supabase
    .from('comments')
    .select('*, user:user(name, avatar_url), comment_likes(user_id)')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Fetch all recent comments error:", error);
    return [];
  }

  const augmented = await augmentCommentsWithFavoriteTeam(data || []);
  
  // 경기 정보 매핑 (post_id가 경기 ID인 경우)
  const results = augmented.map(comment => {
    const rawComment = (data || []).find(d => d.id === comment.id);
    const likes = rawComment?.comment_likes || [];
    return {
      ...comment,
      likes_count: likes.length,
      is_liked: user ? likes.some((l: any) => l.user_id === user.id) : false
    };
  });

  return results;
};

export const reportComment = async (
  commentId: string,
  reason: string
) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('로그인 필요');

  const { error } = await supabase.from('comment_reports').insert({
    comment_id: commentId,
    reporter_id: user.id,
    reason,
  });
  
  if (error) {
    await supabase
      .from('comments')
      .update({ is_reported: true })
      .eq('id', commentId);
  }
};
